import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Controlled Gateway page
const { useState } = React;

const Icon = {
  arrow: (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/></svg>,
};

function Hero() {
  return (
    <section className="section section-dark gw-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <span className="gw-eyebrow"><span className="eb-dot"></span>CONTROLLED GATEWAY · SCOPED EGRESS · AUDITED BY DEFAULT</span>
        <h1>Bounded outbound<br/>for the times you<br/><em>really need it.</em></h1>
        <p className="lead">Sometimes a workflow genuinely needs one bounded fetch against the public internet. Purpose, limits, revoke, and visible history &mdash; not a broad always-on proxy you will regret later.</p>

        <div className="gw-hero-bottom">
          <div className="gw-stats">
            <div className="gw-stat"><div className="big"><em>per-task</em></div><div className="lbl">Sessions, not standing tunnels</div></div>
            <div className="gw-stat"><div className="big"><em>demo</em> region</div><div className="lbl">Representative selector in the product view</div></div>
            <div className="gw-stat"><div className="big"><em>live</em></div><div className="lbl">Session history, usage, and support notes</div></div>
            <div className="gw-stat"><div className="big"><em>Stop</em></div><div className="lbl">Revoke an active task from the dashboard</div></div>
          </div>
          <div className="gw-viz">
            <div className="viz-label">SESSION · TASK ACTIVE-2K7</div>
            <div className="viz-status"><span className="live"></span>LIVE · 00:14:38</div>
            <div style={{ marginTop: 28 }}>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 18, color: 'var(--text-dark-primary)', letterSpacing: '-0.015em', marginBottom: 6 }}>Sync vendor pricing → BigQuery</div>
              <div style={{ fontSize: 11, color: 'var(--text-dark-muted)', letterSpacing: '0.04em' }}>Purpose · finops weekly run</div>
            </div>
            <div className="session-card">
              <div className="session-row"><span className="k">Region</span><span className="v">eu-frankfurt</span></div>
              <div className="session-row"><span className="k">Origin</span><span className="v">1 approved public origin</span></div>
              <div className="session-row"><span className="k">Method cap</span><span className="v">GET, HEAD only</span></div>
              <div className="session-row"><span className="k">Data cap</span><span className="v">800 MB <span style={{ color: 'var(--text-dark-muted)' }}>· 304 MB used</span></span></div>
              <div className="session-row"><span className="k">Request cap</span><span className="v">5,000 / hour</span></div>
              <div className="session-row"><span className="k">Status</span><span className="v acc">healthy · within bounds</span></div>
              <div className="progress-bar"><div className="progress-fill"></div></div>
              <div className="progress-meta"><span>304 / 800 MB</span><span>38% · 1,924 / 5,000 req</span></div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 56, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Start a session <Icon.arrow /></span></a>
          <a href="#workflow" className="btn btn-on-dark btn-lg"><span>How it works</span></a>
        </div>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    { n: '01', t: 'Open a task.', b: 'Name what you\u2019re doing in plain language. &ldquo;Scrape vendor pricing.&rdquo; &ldquo;Reach the partner API.&rdquo; This becomes the audit row everyone sees later.' },
    { n: '02', t: 'Set the bounds.', b: 'Purpose, public origin, region, request cap, data cap, and optional expiry. The current prototype is intentionally narrow, with safe defaults instead of a giant policy matrix.' },
    { n: '03', t: 'Run the bounded task.', b: 'The current product executes a short-lived bounded web task against the approved origin. Anything outside the bounds &mdash; wrong target, wrong method, over cap &mdash; fails closed with a readable result.' },
    { n: '04', t: 'Close the loop.', b: 'The task ends on completion, expiry, or revoke. Session history, usage, and support notes stay visible in the dashboard so operators can see what happened later.' },
  ];
  return (
    <section className="workflow" id="workflow">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">How it works</span>
          <h2>Four primitives. One honest workflow.</h2>
          <p>The whole point is that &ldquo;outbound to the internet&rdquo; should be a <em>task</em>, not a posture. Here&rsquo;s what running one looks like.</p>
        </div>
        <div className="wf-grid">
          {steps.map(s => (
            <div key={s.n} className="wf-step">
              <div className="num">STEP {s.n}</div>
              <h3>{s.t}</h3>
              <p dangerouslySetInnerHTML={{ __html: s.b }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Caps() {
  const items = [
    { tag: 'BOUNDARY', title: 'One approved public origin.', body: 'The current prototype is intentionally narrow: one public <code>http</code> or <code>https</code> origin per task, with relative paths staying under that origin.', vals: ['one origin', 'public http/https', 'relative paths'] },
    { tag: 'BUDGET', title: 'Data and request caps.', body: 'Stop runaway jobs at the bounded task layer, not after the bill lands. Request and byte quotas are part of the current prototype.', vals: ['byte cap', 'request cap', 'optional expiry'] },
    { tag: 'GEOGRAPHY', title: 'Region is part of the task.', body: 'The current product already treats region choice as part of a bounded session, but this page should not promise a broad public region catalog beyond the Business early-access surface we actually have today.', vals: ['region selector', 'bounded task', 'Business surface'] },
    { tag: 'METHODS', title: 'Read-oriented by default.', body: 'The current prototype is intentionally conservative: approved outbound web tasks are limited to <code>GET</code> and <code>HEAD</code>. This is not the place to market a general write-capable proxy yet.', vals: ['GET', 'HEAD', 'fail closed'] },
    { tag: 'ABUSE', title: 'Abuse state is visible.', body: 'Repeated failed executions and sustained byte volume can raise a session into a flagged state. Operators can see that history in the current product instead of discovering it after the fact.', vals: ['flagged', 'usage history', 'revoke'] },
    { tag: 'PURPOSE', title: 'A stated purpose, in plain English.', body: 'The audit row reads &ldquo;Sync vendor pricing,&rdquo; not &ldquo;task-2k7.&rdquo; You&rsquo;ll thank us at compliance review.', vals: ['plain text', 'searchable', 'mandatory'] },
  ];
  return (
    <section className="caps">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="section-h">
          <span className="section-label">Bounds, six ways</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>Every session has guardrails. <br/>You write them in advance, not after.</h2>
          <p>Six dials per task. Each one defaults to the safe value. None of them are optional.</p>
        </div>
        <div className="caps-grid">
          {items.map((c, i) => (
            <div key={i} className="cap">
              <div className="tag">{c.tag}</div>
              <h4>{c.title}</h4>
              <p dangerouslySetInnerHTML={{ __html: c.body }} />
              <div className="vals">{c.vals.map((v, j) => <span key={j} className="pill">{v}</span>)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AuditLog() {
  const rows = [
    { time: '14:38:02', verb: 'GET', url: 'pricing.vendor.io/v2/skus?cursor=4f2a', size: '142 KB', status: 'ok', cls: '' },
    { time: '14:38:01', verb: 'GET', url: 'pricing.vendor.io/v2/skus?cursor=4f29', size: '138 KB', status: 'ok', cls: '' },
    { time: '14:37:59', verb: 'POST', url: 'pricing.vendor.io/v2/_internal/admin', size: '—', status: 'denied', cls: 'deny' },
    { time: '14:37:55', verb: 'GET', url: 'cdn.vendor.io/static/logo.svg', size: '4 KB', status: 'denied', cls: 'deny' },
    { time: '14:37:48', verb: 'GET', url: 'pricing.vendor.io/v2/skus?cursor=4f28', size: '141 KB', status: 'ok', cls: '' },
    { time: '14:37:42', verb: 'GET', url: 'pricing.vendor.io/v2/skus?cursor=4f27', size: '139 KB', status: 'ok', cls: '' },
    { time: '14:37:39', verb: 'GET', url: 'pricing.vendor.io/v2/skus?cursor=4f26', size: '12 MB', status: '> cap', cls: 'warn' },
  ];
  return (
    <section className="audit">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Every request, every reason</span>
          <h2>Visible session history matters.</h2>
          <p>The current product already exposes session history, usage, and support notes. The table below is a representative demo of the kind of operator view this bounded task surface is built around.</p>
        </div>
        <div className="audit-table">
          <div className="audit-head">
            <span>TIME</span><span>METHOD</span><span>URL</span><span>SIZE</span><span>STATUS</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="audit-row">
              <span className="time">{r.time}</span>
              <span className="verb">{r.verb}</span>
              <span className="url">{r.url}</span>
              <span className="size">{r.size}</span>
              <span className={`status ${r.cls}`}>{r.status}</span>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-light-muted)', marginTop: 16, letterSpacing: '0.04em' }}>
          Representative session-history demo · current product truth: visible events and bounded usage are real; polished export or webhook delivery is not the public claim yet
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const cases = [
    { n: '01', t: 'Vendor data syncs', b: 'Weekly pricing pulls, partner inventory feeds, the script that lives on a cron and you barely look at &mdash; bounded, audited, killable.' },
    { n: '02', t: 'AI agent egress', b: 'When your agent needs to actually fetch a webpage, give it a session with allowlist and caps, not a credit card and a wide-open proxy.' },
    { n: '03', t: 'CI checks reaching out', b: 'Read-oriented health checks, vendor fetches, or one-off bounded validation against a public endpoint. Each kind of outward job gets its own limits and history.' },
    { n: '04', t: 'Web scraping with limits', b: 'When you genuinely need bounded outbound fetches, the point is to keep them narrow, visible, and revocable &mdash; not to pretend this is a mass-market scraping network.' },
    { n: '05', t: 'Migration windows', b: 'A two-hour task that needs to talk to a legacy SOAP endpoint exactly once. Open it, run it, close it. Don\u2019t leave the door open.' },
    { n: '06', t: 'Compliance-friendly testing', b: 'Auditors love &ldquo;every outbound call from prod is logged with stated purpose.&rdquo; This is how you give them that, without re-architecting.' },
  ];
  return (
    <section className="uc-section">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Where it earns its keep</span>
          <h2>The honest list of jobs<br/>this is good for.</h2>
          <p>If your egress problem is on this list, the gateway is built for it. If it isn&rsquo;t, please tell us &mdash; we keep this list short on purpose.</p>
        </div>
        <div className="uc-grid">
          {cases.map((c, i) => (
            <div key={i} className="uc">
              <div className="uc-tile">{c.n}</div>
              <h4>{c.t}</h4>
              <p>{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Compare() {
  const rows = [
    { l: 'Task-scoped egress', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'Single approved public origin', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'GET/HEAD-only prototype', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'Request + byte caps', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'Visible session history', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'Stated purpose field', a: 'mandatory', b: 'rare', c: 'rare', win: 'a' },
    { l: 'Revoke from dashboard', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
    { l: 'Operator support notes', a: 'yes', b: 'varies', c: 'varies', win: 'a' },
  ];
  return (
    <section className="compare">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="section-h">
          <span className="section-label">Compared</span>
          <h2 style={{ color: 'var(--text-dark-primary)' }}>Versus the proxy you might already have.</h2>
          <p>This comparison is intentionally conservative. It highlights the bounded-task behaviors we actually ship today, not a fantasy full-proxy platform.</p>
        </div>
        <div className="compare-table">
          <div className="ct-row head">
            <span></span>
            <span className="col-h">Controlled Gateway</span>
            <span>Common proxy setup</span>
            <span>DIY tunnel setup</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="ct-row">
              <span className="row-l">{r.l}</span>
              <span className={`ct-val ${r.win === 'a' ? 'win' : ''}`}>{r.a}</span>
              <span className="ct-val">{r.b}</span>
              <span className="ct-val">{r.c}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'Is this a tunnel product?', a: 'No. The current public claim is much narrower: this is an early-access bounded web-task surface for approved public <code>http</code> or <code>https</code> targets. If you need broader private connectivity, that is a different product story.' },
    { q: 'What does the current prototype actually allow?', a: 'One approved public origin per task, relative paths under that origin, plus bounded request and byte usage. The current prototype is intentionally conservative and limited to <code>GET</code> and <code>HEAD</code> requests.' },
    { q: 'How fast is the kill switch?', a: 'The product already lets owners or admins revoke active tasks from the dashboard and inspect what happened afterward. This page should not promise sub-second global timing numbers we have not made into a public contract.' },
    { q: 'Can I share one task across multiple machines?', a: 'That is not the public product claim today. The safe claim is that the current bounded-task workflow is created, run, inspected, and revoked from the Business gateway surface.' },
    { q: 'What about authenticated APIs?', a: 'Approved public API targets are a valid use case for the current bounded web-task model, as long as the task stays inside the current limits and target restrictions.' },
    { q: 'Does this replace ZScaler or Netskope?', a: 'No, complementary. SWGs sit on the user laptop and police what humans browse to. The Controlled Gateway sits on the server side and polices what your scripts and agents reach. Different threat model, different tool.' },
    { q: 'How does pricing work?', a: 'The honest public claim today is that bounded egress is a Business-only advanced surface in early access. We should not market a mass-market free tier for this page before the packaging and launch details are actually published.' },
    { q: 'Can I run this entirely in my own VPC?', a: 'That is not a current public launch claim. The supported story today is the named.network Business surface, with broader dedicated-gateway or custom-deployment options left for later packaging decisions.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>Eight common questions.</h2>
          <p>Anything not covered? Email us &mdash; we&rsquo;ll add the answer here.</p>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a" dangerouslySetInnerHTML={{ __html: f.a }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Ready when you are</span>
          <h2>Outbound, but on purpose.<br/><em>One session. One task. One log.</em></h2>
          <p>Business unlocks the bounded egress surface, with early-access guardrails, visible history, and operator controls instead of a broad public proxy promise.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a Business workspace <Icon.arrow /></span></a>
            <a href="/contact" className="btn btn-on-dark btn-lg"><span>Talk to us first</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <>
      <SiteNav />
      <Hero />
      <Workflow />
      <Caps />
      <AuditLog />
      <UseCases />
      <Compare />
      <FAQ />
      <CTA />
      <SiteFooter />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

